import { useState, Fragment, useContext } from 'react';
import {
  any, number, object, string,
} from 'prop-types';
import dynamic from 'next/dynamic';
import {
  Row, Button, Col, Comment, Progress, Rate,
} from 'antd';
import { CaretLeftOutlined, CaretRightOutlined, CloseOutlined } from '@ant-design/icons';

import { getReviews } from 'actions/products';
import { RateSVG } from 'components/iconsSVG/rate';
import { i18nContext } from 'context/i18n';

import { timeSince } from 'utils/helpers';
import { dataLayerHandleEvent } from 'utils/dataLayers';

import styles from './style.module.css';

const Slider = dynamic(() => import('components/slider'));
const SortReviews = dynamic(() => import('components/sortReviews'));
const rates = [ 5, 4, 3, 2, 1 ];

function ProductReviews({
  productCode, firstReviews, reviewStatistics, reviewsRef, locale, averageRate, line,
}) {
  const { t, profileData } = useContext(i18nContext);
  const [ currentReviewFilters, setCurrentReviewFilters ] = useState([]);
  const [ currentSort, setCurrentSort ] = useState([ 'rating', 'desc' ]);
  const [ filteredReviews, setFilteredReviews ] = useState(false);

  const reviews = !filteredReviews ? firstReviews : filteredReviews;
  const totalReviewsString = `${reviews.Offset + 1}–${(reviews.Offset + reviews.Limit) < reviews.TotalResults ? reviews.Offset + reviews.Limit : reviews.TotalResults} ${t('ficha.producto', 'of', 'of')} ${reviews.TotalResults} ${t('ficha.producto', 'resenas', 'Reviews')}`;

  const handleGetReviews = async (offset, sort, filter) => {
    const [ sortField, sortType ] = sort;
    let reviews_lang = [];

    if (profileData?.config) {
      const { config } = profileData;

      if (config?.reviews && config?.reviews_lang) {
        ({ reviews_lang } = config);
      }
    }
    const result = await getReviews({
      lang: locale, productCode, offset, sortField, sortType, filter, langList: reviews_lang,
    });
    setFilteredReviews(result.BatchedResults.q1);
  };

  const handleFilterReviews = (value) => {
    let currentFilter = [ ...currentReviewFilters ];

    if (value === true) {
      currentFilter = [];
    } else if (currentFilter.includes(value)) {
      const i = currentFilter.indexOf(value);
      currentFilter.splice(i, 1);
    } else {
      currentFilter.push(value);
    }

    setCurrentReviewFilters(currentFilter);
    handleGetReviews(0, currentSort, currentFilter);
  };

  const handleClickSort = (sortClicked) => {
    const [ sortType, sortValue ] = sortClicked;
    const sortMap = {
      rating: {
        desc: 'highest to lower rating',
        asc: 'lower to highest rating',
      },
      SubmissionTime: {
        desc: 'most recent',
        asc: 'oldest',
      },
    };

    const eventValue = sortMap?.[sortType]?.[sortValue];

    if (eventValue) {
      dataLayerHandleEvent({
        event: 'item_reviews',
        event_category: 'product detail',
        event_detail_1: 'sort by',
        event_detail_2: eventValue,
      });
    }

    setCurrentSort(sortClicked);
    handleGetReviews(0, sortClicked, currentReviewFilters);
  };

  const reviewsTitle = t('ficha.producto', 'resenas.sobre', '***Reviews about <TAG line>');

  return (
      <div className={styles.productReviews}>
        <h2 className={styles.reviewsHeader} ref={reviewsRef}>{reviewsTitle.replace('<TAG line>', line)}</h2>
        <div className={styles.sectionRow}>
          <div>
            <p className={styles.reviewsDescription}>{t('ficha.producto', 'selecciona.resenas', 'Select a rating below to filter reviews.')} </p>
            {reviewStatistics?.RatingDistribution && rates.map((rate) => {
              const filteredRate = reviewStatistics.RatingDistribution.filter((distributionRate) => distributionRate.RatingValue === rate);
              const count = filteredRate.length > 0 ? filteredRate[0].Count : 0;
              const totalCount = reviewStatistics.TotalReviewCount || 0;
              const rateValue = filteredRate.length > 0 ? filteredRate[0].RatingValue : rate;

              let filterStyle = `${styles.progressReviewsWrapper} ${styles.reviewsDescription}`;

              if (count === 0) {
                filterStyle = `${filterStyle} ${styles.noPointer}`;
              }

              return (
                <div
                  key={rate}
                  className={filterStyle}
                  onClick={count > 0 ? () => handleFilterReviews(rateValue) : () => true }
                >
                  <span>{rateValue}</span><span className={styles.progressRate}>
                    <RateSVG className={styles.progressReviewsStar}/>
                  </span>
                  <Progress
                    strokeLinecap="square"
                    strokeWidth={7}
                    trailColor="#e3e3e3"
                    percent={(count * 100) / totalCount}
                    showInfo={false}
                    className={styles.progressReviews}
                  />
                  <span>{count}</span>
                </div>
              );
            })}
          </div>
          <div>
            <p className={styles.reviewsDescription}>{t('ficha.producto', 'valoracion.promedio.cliente', 'Average customer rating')} </p>
            <Row>
              <div className={styles.rating}>
                <div className={styles.labelAverageReview}>
                {t('ficha.producto', 'general', 'General')}
                </div>
                <div className={styles.reviewsDescription}>
                  <div className={styles.rateWrapper}>
                    <Rate
                      className={styles.rateStarsAverage}
                      allowHalf
                      defaultValue={averageRate}
                      value={averageRate}
                      disabled
                      character={<RateSVG />}
                    />
                  </div>
                </div>
                <span className={styles.labelAverageReview}>{averageRate}</span>
              </div>
              <div className={styles.rattingAverageWrapper}>
                {reviewStatistics?.SecondaryRatingsAveragesOrder.length > 0 && reviewStatistics.SecondaryRatingsAveragesOrder.map((averageType) => (
                  <Fragment key={averageType}>
                    <div>
                      <div className={styles.labelAverageReview}>
                        {averageType === 'HowIsTheFit' ? `${t('ficha.producto', 'ajuste', 'Fit')} ` : `${t('ficha.producto', 'ancho', 'Width')} `}
                      </div>
                      <div>
                        {reviewStatistics.SecondaryRatingsAverages[averageType].DisplayType === 'SLIDER' &&
                          <Slider
                            defaultValue={Math.round((reviewStatistics.SecondaryRatingsAverages[averageType].AverageRating - 1) * 100) / 4}
                            labelMin={averageType === 'HowIsTheFit' ? `${t('ficha.producto', 'pequeno', 'Small')}` : `${t('ficha.producto', 'estrcho', 'Narrow')}`}
                            labelMax={averageType === 'HowIsTheFit' ? `${t('ficha.producto', 'grande', 'Large')}` : `${t('ficha.producto', 'holgado', 'Wide')}`}
                            disabled
                          />
                        }
                      </div>
                    </div>
                  </Fragment>
                ))}
              </div>
            </Row>
          </div>
        </div>
          {reviews.TotalResults > 0 &&
            <Col span={24}>
              <p className={styles.reviewsResultsRow}>
                <span>{totalReviewsString}</span>
                <SortReviews onClick={handleClickSort} />
              </p>
              <Col span={20} className={styles.reviewsFilterWrapper}>
                {
                  currentReviewFilters.sort().map((item, index) => (
                    <Button className={styles.reviewsFilterItem} onClick={() => handleFilterReviews(item)} type="primary" key={`CRF-${index}`}>
                      {item} {t('ficha.producto', 'estrellas', 'stars')}
                      <CloseOutlined className={styles.filterIcon} />
                    </Button>
                  ))
                }
                {
                  currentReviewFilters.length > 0 &&
                  <Button className={styles.reviewsFilterItem} onClick={() => handleFilterReviews(true)} type="primary">
                    {t('ficha.producto', 'eliminar.todos', 'Erase all')}
                    <CloseOutlined className={styles.filterIcon} />
                  </Button>
                }
              </Col>
            </Col>
          }
        <div className={styles.reviewsWrapper}>
          {reviews.Results.map((review, index) => (
            <Fragment key={`${review.user} ${index}`}>
              <div>
                {
                  review.IsRatingsOnly ?
                  <Comment
                    content={
                      <>
                        <h3>1 {t('ficha.producto', 'valoracion.sin.resena', 'rating without review')}</h3>
                        {/*
                        PENDIENTE DE COMENTAR CÓMO SERÁN EN EL NUEVO DISEÑO
                        (N valoraciones sin reseña) ||
                        (Valoración con estrellas, nombre y fecha con texto de "Sin reseña")
                        */}
                      </>
                    }
                  /> :
                  <Comment
                    author={
                      <div className={styles.rateWrapper}>
                        <Rate
                          className={styles.rateStars}
                          allowHalf
                          defaultValue={review.Rating}
                          value={review.Rating}
                          disabled
                          character={<RateSVG />}
                        />
                        <span className={styles.rateAuthor}>{review.UserNickname === null ? t('ficha.producto', 'anonimo', 'Anonymous') : review.UserNickname}</span>
                      </div>
                    }
                    datetime={<span className={styles.reviewDatetime}>{`· ${timeSince(review.SubmissionTime)}`}</span>}
                    content={
                      <>
                        <p className={styles.reviewTitle}>{review.Title}</p>
                        <p className={styles.reviewsText}>
                          {review.ReviewText}
                        </p>
                        {review.Origin &&
                          <p className={`${styles.originalPublishedAt} ${styles.reviewTitle}`}>
                            {t('ficha.producto', 'publicado.por', 'Originally posted on')} {review.Origin}
                          </p>
                        }
                      </>
                    }
                  />
                }
              </div>
              <div className={styles.ratingWrapper}>
                {review.SecondaryRatingsOrder.length > 0 && review.SecondaryRatingsOrder.map((rateType) => (
                  <Fragment key={rateType}>
                      <div>
                        <p className={styles.labelReviewAdjustment}>{rateType === 'HowIsTheFit' ? `${t('ficha.producto', 'ajuste', 'Fit')}` : `${t('ficha.producto', 'ancho', 'Width')} `}</p>
                        <Slider
                          defaultValue={Math.round((review.SecondaryRatings[rateType].Value - 1) * 100) / 4}
                          labelMin={rateType === 'HowIsTheFit' ? `${t('ficha.producto', 'pequeno', 'Small')}` : `${t('ficha.producto', 'estrcho', 'Narrow')}`}
                          labelMax={rateType === 'HowIsTheFit' ? `${t('ficha.producto', 'grande', 'Large')}` : `${t('ficha.producto', 'holgado', 'Wide')}`}
                          disabled
                        />
                      </div>
                  </Fragment>
                ))}
              </div>
            </Fragment>
          ))}
        </div>
        {reviews.TotalResults > 0 &&
            <Col span={24}>
              <div className={styles.reviewsResultsRow}>
                <span>{totalReviewsString}</span>
                <div>
                  <Button
                    block
                    icon={<CaretLeftOutlined />}
                    size="small"
                    disabled={reviews.Offset === 0}
                    type="primary"
                    onClick={() => handleGetReviews(reviews.Offset - 8 < 0 ? 0 : reviews.Offset - 8, currentSort, currentReviewFilters)}
                  />
                  <Button
                    block
                    icon={<CaretRightOutlined />}
                    size="small"
                    disabled={reviews.Offset + reviews.Limit >= reviews.TotalResults}
                    type="primary"
                    onClick={() => handleGetReviews(reviews.Offset + 8, currentSort, currentReviewFilters)}
                  />
                </div>
              </div>
            </Col>
          }
      </div>
  );
}

ProductReviews.propTypes = {
  reviewStatistics: object,
  firstReviews: object,
  productCode: string.isRequired,
  locale: string,
  averageRate: number,
  reviewsRef: any,
  line: string,
};

ProductReviews.defaultProps = {
  reviewStatistics: {},
};

export default ProductReviews;
